import { useRef, useState } from "react";
import { api, ApiError } from "./api";
import { t, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Liitoslistan muokkauskierto — työnjohdon polku.

   Vie Excel → korjaa → tuo takaisin. Sama mekanismi kuin
   hitsauslokissa (`Muokkauskierto`, api/src/muokkauskierto.ts), eri
   sarakkeisto. Tuonti on kaksivaiheinen: kuivakäynti näyttää mitä
   tapahtuisi, vahvistus tallentaa.

   **Tyhjä solu ei muuta mitään.** Kesken jäänyt kierros on työmaan
   normaali tila, eikä puolivalmis taulukko saa pyyhkiä sitä mikä on
   jo kirjattu.

   Rivi ilman `liitos_id`:tä mutta tunnuksella on uusi liitos — näin
   listan valmistelu on Excelin työtä eikä lomakkeen.

   Kuittaussarakkeet ovat vain luettavaksi: kuittaus asetetaan
   yhtenä ryhmänä ja se on asentajan polku.
   ═══════════════════════════════════════════════════════════ */

type Tulos = Awaited<ReturnType<typeof api.tuoLiitokset>>;

export default function LiitosKierto({ laiteId, valmis }: { laiteId: number; valmis: () => void }) {
  const [auki, setAuki] = useState(false);
  const [sisalto, setSisalto] = useState<string | null>(null);
  const [nimi, setNimi] = useState("");
  const [esikatselu, setEsikatselu] = useState<Tulos | null>(null);
  const [tallennettu, setTallennettu] = useState<Tulos | null>(null);
  const [tyoskentelee, setTyoskentelee] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function nollaa() {
    setSisalto(null); setNimi(""); setEsikatselu(null);
    setTallennettu(null); setVirhe(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function valitse(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setVirhe(null); setEsikatselu(null); setTallennettu(null);
    const lukija = new FileReader();
    lukija.onload = async () => {
      const data = String(lukija.result);
      setSisalto(data); setNimi(f.name);
      setTyoskentelee(true);
      try {
        setEsikatselu(await api.tuoLiitokset(laiteId, data, false));
      } catch (err) {
        setVirhe(err instanceof ApiError ? virheTeksti(err.virhe, err.message) : t("muu.pohja.luku_virhe"));
      } finally { setTyoskentelee(false); }
    };
    lukija.readAsDataURL(f);
  }

  async function vahvista() {
    if (!sisalto) return;
    setTyoskentelee(true); setVirhe(null);
    try {
      setTallennettu(await api.tuoLiitokset(laiteId, sisalto, true));
      setEsikatselu(null);
      valmis();
    } catch (err) {
      setVirhe(err instanceof ApiError ? virheTeksti(err.virhe, err.message) : t("ltar.tallennus_virhe"));
    } finally { setTyoskentelee(false); }
  }

  const muutoksia = (esikatselu?.kenttamuutoksia ?? 0) > 0 || (esikatselu?.uusia ?? 0) > 0;

  return (
    <section className="panel" style={{ marginTop: 12 }}>
      <div className="panel__head">
        <h2>{t("muu.lk.otsikko")}</h2>
        <button className="nappi nappi--kevyt" onClick={() => { setAuki(!auki); if (auki) nollaa(); }}>
          {auki ? t("hl.sulje") : t("qr.avaa")}
        </button>
      </div>

      {auki && (
        <div style={{ padding: "4px 2px" }}>
          <p className="hint">
            {t("muu.lk.selite1")} <strong>{t("muu.lk.selite2")}</strong>{" "}
            {t("muu.lk.selite3")}
          </p>

          <p>
            <a className="nappi nappi--kevyt" href={api.liitospohjaUrl(laiteId)}>
              {t("muu.lk.vie")}
            </a>
          </p>

          <p>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={valitse} disabled={tyoskentelee} />
            {nimi && <span className="dim"> {nimi}</span>}
          </p>

          {virhe && <p className="virhe">{virhe}</p>}
          {tyoskentelee && <p>{t("muu.pohja.kasitellaan")}</p>}

          {esikatselu && (
            <div>
              <p>
                {t("muu.lk.tasmasi", { n: esikatselu.rivit ?? 0 })}
                {(esikatselu.tuntematon ?? 0) > 0 &&
                  <> · <strong>{esikatselu.tuntematon}</strong> {t("muu.lk.tuntematonta")}</>}
                {" · "}{t("muu.lk.kenttamuutosta", { n: esikatselu.kenttamuutoksia ?? 0 })}
                {(esikatselu.uusia ?? 0) > 0 && <> · <strong>{esikatselu.uusia}</strong> {t("muu.lk.uutta")}</>}
              </p>

              {(esikatselu.esikatselu?.length ?? 0) > 0 && (
                <table className="taulu">
                  <thead><tr><th>{t("muu.lk.liitos")}</th><th>{t("muu.pohja.kentta")}</th><th>{t("muu.lk.nykyinen")}</th><th>{t("muu.hlm.uusi")}</th></tr></thead>
                  <tbody>
                    {esikatselu.esikatselu!.map((m, i) => (
                      <tr key={i}>
                        <td className="mono">#{m.id}</td><td>{m.kentta}</td>
                        <td className="dim">{m.vanha || "—"}</td><td>{m.uusi}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {(esikatselu.uudet_esikatselu?.length ?? 0) > 0 && (
                <table className="taulu">
                  <thead><tr><th>{t("muu.lk.uusi_liitos")}</th><th>{t("muu.lk.kuvaus")}</th><th>{t("muu.lk.piiri")}</th><th>{t("muu.lk.momentti")}</th></tr></thead>
                  <tbody>
                    {esikatselu.uudet_esikatselu!.map((u, i) => (
                      <tr key={i}>
                        <td className="mono">{u.tunnus}</td><td>{u.kuvaus ?? "—"}</td>
                        <td>{u.piiri}</td><td>{u.vaadittu_nm ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Ei muutoksia on tulos siinä missä muutokset: se kertoo
                  että tiedosto luettiin ja se vastasi kantaa. */}
              {!muutoksia && <p>{t("muu.lk.ei_muutoksia")}</p>}
              {muutoksia && (
                <button className="nappi" disabled={tyoskentelee} onClick={vahvista}>
                  {t("muu.lk.tallenna")}
                </button>
              )}
            </div>
          )}

          {tallennettu && (
            <p className="kuittaus-ok">
              ✓ {t("muu.lk.paivitetty", { n: tallennettu.paivitetty ?? 0 })}
              {(tallennettu.luotu ?? 0) > 0 && <>, {t("muu.lk.uutta_n", { n: tallennettu.luotu ?? 0 })}</>}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
